import type {
  ChargeType,
  Currency,
  QuoteTaskChargePublic,
  QuoteTaskDetailPublic,
  UpdateQuote,
} from "@common/client"
import { toNumberOrNull } from "@common/lib/number"
import {
  addCharge,
  underCharge,
} from "@/routes/_layout/quoting_/quotes/-lib/ops"

export type LabourMethod = "tm" | "flat" | "none"

export type FlatMode = "hour" | "total"

export const FLAT_TOTAL_UNIT = "job"

export type LabourCharge = Pick<
  QuoteTaskChargePublic,
  "type" | "quantity" | "sale_unit_price" | "cost_quantity" | "unit_of_measure"
>

export type LabourFields = {
  type?: "labour" | "labour_flat_rate"
  quantity?: string | null
  sale_unit_price?: string | null
  unit_of_measure?: string | null
  cost_quantity?: string | null
}

export function flatMode(charge: LabourCharge): FlatMode {
  return charge.type === "labour_flat_rate" &&
    charge.unit_of_measure === FLAT_TOTAL_UNIT
    ? "total"
    : "hour"
}

export function flatModeFields(
  charge: LabourCharge,
  next: FlatMode,
  cardRate: string | null,
): LabourFields {
  const hours = toNumberOrNull(charge.quantity)
  const rate = toNumberOrNull(charge.sale_unit_price)
  return next === "total"
    ? {
        quantity: "1",
        unit_of_measure: FLAT_TOTAL_UNIT,
        cost_quantity: charge.quantity,
        sale_unit_price:
          hours !== null && rate !== null ? (hours * rate).toFixed(2) : null,
      }
    : {
        quantity: charge.cost_quantity,
        unit_of_measure: null,
        cost_quantity: null,
        sale_unit_price: hourlyRate(charge) ?? chargedRate(cardRate),
      }
}

export function switchFlatMode(
  taskId: string,
  charge: QuoteTaskChargePublic,
  next: FlatMode,
  cardRate: string | null,
): UpdateQuote {
  return underCharge(taskId, charge.id, flatModeFields(charge, next, cardRate))
}

function hourlyRate(charge: LabourCharge): string | null {
  const hours = toNumberOrNull(charge.cost_quantity)
  const total = toNumberOrNull(charge.sale_unit_price)
  return hours && total !== null ? (total / hours).toFixed(2) : null
}

const LABOUR_TYPES: ChargeType[] = ["labour", "labour_flat_rate"]

export function labourCharge(
  task: QuoteTaskDetailPublic,
): QuoteTaskChargePublic | null {
  return (
    task.active_charges.find((charge) => LABOUR_TYPES.includes(charge.type)) ??
    null
  )
}

export function labourCharges(
  task: QuoteTaskDetailPublic,
): QuoteTaskChargePublic[] {
  return task.active_charges.filter((charge) =>
    LABOUR_TYPES.includes(charge.type),
  )
}

export function labourHours(task: QuoteTaskDetailPublic): number | null {
  const hours = labourCharges(task)
    .map((charge) =>
      toNumberOrNull(
        flatMode(charge) === "total" ? charge.cost_quantity : charge.quantity,
      ),
    )
    .filter((value) => value !== null)
  return hours.length > 0 ? hours.reduce((sum, value) => sum + value, 0) : null
}

export function labourMethod(charge: LabourCharge | null): LabourMethod | null {
  if (charge === null) return null
  if (toNumberOrNull(charge.sale_unit_price) === 0) return "none"
  return charge.type === "labour_flat_rate" ? "flat" : "tm"
}

export function labourDone(charge: LabourCharge | null): boolean {
  const method = labourMethod(charge)
  if (charge === null || method === null) return false
  if (method === "none") return true
  const price = toNumberOrNull(charge.sale_unit_price) ?? 0
  const hours = toNumberOrNull(charge.quantity) ?? 0
  return price > 0 && hours > 0
}

function chargedRate(cardRate: string | null): string | null {
  return toNumberOrNull(cardRate) ? cardRate : null
}

function currentRate(current: LabourCharge | null): string | null {
  return current && labourMethod(current) !== "none"
    ? current.sale_unit_price
    : null
}

export function labourFields(
  method: LabourMethod,
  current: LabourCharge | null,
  cardRate: string | null,
): LabourFields & { type: "labour" | "labour_flat_rate" } {
  const total = current !== null && flatMode(current) === "total"
  const quantity = total ? current.cost_quantity : (current?.quantity ?? null)
  const rate = total ? chargedRate(hourlyRate(current)) : currentRate(current)
  const reset = total ? { unit_of_measure: null, cost_quantity: null } : {}
  const card = chargedRate(cardRate)
  switch (method) {
    case "tm":
      return {
        type: "labour",
        quantity,
        sale_unit_price: card ?? rate,
        ...reset,
      }
    case "flat":
      return {
        type: "labour_flat_rate",
        quantity,
        sale_unit_price: card ?? rate,
        ...reset,
      }
    case "none":
      return {
        type:
          current?.type === "labour_flat_rate" ? "labour_flat_rate" : "labour",
        quantity: current?.quantity ?? null,
        sale_unit_price: "0",
      }
  }
}

export function switchLabour(
  task: QuoteTaskDetailPublic,
  method: LabourMethod,
  cardRate: string | null,
  currency: Currency,
): UpdateQuote {
  const current = labourCharge(task)
  const next = labourFields(method, current, cardRate)
  return current === null
    ? addCharge(task.id, { currency, ...next })
    : underCharge(task.id, current.id, { currency, ...next })
}
